import { ConversationEntry } from './project';
import { LiveState } from './live';

export type MeetingStatus = 'draft' | 'prepared' | 'processing' | 'completed' | 'archived';

export interface MeetingAttendee {
  attendee_id: string;
  name: string;
  role?: string | null;
  organization?: string | null;
  stance?: string | null;
  is_generated: boolean;
  // Links the attendee to a real user account. When set, that user can see the
  // meeting once it is Prepared or later (visibility is matched on this id).
  user_id?: string | null;
}

export interface AgendaItem {
  item_id?: string;
  title: string;
  description?: string | null;
  duration_minutes?: number | null;
  talking_points?: string[];
}

export interface MeetingAgenda {
  items: AgendaItem[];
  generated_at?: string | null;
  user_modified: boolean;
}

export interface MeetingDecision {
  title: string;
  moved_by?: string | null;
  outcome: string;
}

export interface ActionItem {
  // Stable server-assigned id, backfilled on the meeting's next save or
  // approval. Null means "no identity yet": use it as a render key only, and
  // never store or compare it.
  action_id?: string | null;
  description: string;
  // Display name of the owner, and the only owner information legacy actions
  // carry.
  owner?: string | null;
  // Set when the owner was picked from the meeting's attendees. Null for group
  // owners, external parties, and attendees with no linked login.
  owner_user_id?: string | null;
  owner_attendee_id?: string | null;
  due_date?: string | null;
  status: string;
}

export interface MeetingSummary {
  overview?: string | null;
  key_points: string[];
  decisions: MeetingDecision[];
  action_items: ActionItem[];
  generated_at?: string | null;
  // Approval state (Unit 10d). Editing an approved summary resets approval, so
  // export is blocked until it is re-approved. Absent on older backends.
  approved?: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
}

// Full-replace payload for PUT /meetings/{id}/summary (snake_case). Array order
// is meaning-bearing and is persisted as sent.
export interface UpdateSummaryRequest {
  overview?: string | null;
  key_points: string[];
  decisions: MeetingDecision[];
  action_items: ActionItem[];
}

export interface MeetingBriefing {
  content: string;
  generated_at?: string | null;
}

// A per-user, custom-instructions-framed briefing (distinct from the shared
// Meeting.briefing). Tailored to the calling user's profile custom instructions.
export interface PersonaBriefing {
  id: string;
  meeting_id: string;
  user_id: string;
  steer?: string | null;
  content: string;
  generated_at?: string | null;
}

// Thrown by updateSummary when the meeting was changed by someone else since
// the caller loaded it. Carries the server's current state so the UI can offer
// a reload without discarding the editor's unsaved draft.
export class OutcomesConflictError extends Error {
  readonly currentMeeting: Meeting | null;

  constructor(message: string, currentMeeting: Meeting | null) {
    super(message);
    this.name = 'OutcomesConflictError';
    this.currentMeeting = currentMeeting;
  }
}

export interface Meeting {
  id: string;
  meeting_id: string;
  // Cosmos concurrency token. Echoed back as If-Match when editing outcomes so
  // a stale save is rejected rather than silently overwriting another editor.
  _etag?: string | null;
  title: string;
  description?: string | null;
  project_id?: string | null;
  scheduled_at?: string | null;
  duration_minutes: number;
  location?: string | null;
  committee?: string | null;
  teams_join_url?: string | null;
  attendees: MeetingAttendee[];
  status: MeetingStatus;
  live_state?: LiveState | null;
  agenda?: MeetingAgenda | null;
  briefing?: MeetingBriefing | null;
  transcript_text?: string | null;
  transcript_is_simulated: boolean;
  summary?: MeetingSummary | null;
  conversation_history: ConversationEntry[];
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
}

export interface CreateMeetingRequest {
  title: string;
  description?: string;
  project_id?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  location?: string;
  committee?: string;
  teams_join_url?: string;
  attendees?: Partial<MeetingAttendee>[];
  metadata?: Record<string, any>;
}

export interface UpdateMeetingRequest {
  title?: string;
  description?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  location?: string;
  committee?: string;
  /** Empty string clears the link; omit to leave unchanged. */
  teams_join_url?: string;
  attendees?: MeetingAttendee[];
  status?: MeetingStatus;
  metadata?: Record<string, any>;
}

export interface MeetingChatResponse {
  meeting_id: string;
  query: string;
  answer: string;
  status: string;
}

/**
 * A preparation note the signed-in user left against a meeting material.
 *
 * Private to its author: the API only ever returns your own notes, so nothing
 * here needs to render conditionally on authorship, and there is no sharing
 * control to show.
 */
export interface PrepComment {
  id: string;
  meeting_id: string;
  file_id: string;
  /** 1-based page anchor. Null only when is_document_level is true. */
  page_number: number | null;
  /** True for a material with no inline rendering, and so no page to pin to. */
  is_document_level: boolean;
  author_user_id: string;
  author_name?: string | null;
  body: string;
  created_at: string;
  updated_at?: string | null;
}

/**
 * Minimal provenance for an action carried over from an earlier meeting.
 *
 * Deliberately carries no meeting id: a user may be shown an action from a
 * meeting they cannot open, so this must not become navigation into it.
 */
export interface CarriedOverSource {
  title: string;
  scheduled_at?: string | null;
  committee?: string | null;
}

export interface CarriedOverAction {
  action: ActionItem;
  source: CarriedOverSource;
}

/**
 * A task the secretary assigned to an attendee while preparing for the meeting.
 *
 * Separate from ActionItem, which records what the meeting resolved: an
 * assignee may close one of these without holding write access to the minutes.
 */
export interface PrepAction {
  prep_action_id: string;
  description: string;
  assignee_user_id: string;
  assignee_name?: string | null;
  due_date?: string | null;
  /** 'open' or 'completed'. */
  status: string;
  created_by?: string | null;
  created_at: string;
  completed_at?: string | null;
  completed_by?: string | null;
}

export interface PrepActionsResponse {
  this_meeting: ActionItem[];
  carried_over: CarriedOverAction[];
  // Prep tasks the secretary raised ahead of this meeting.
  prep_assigned: PrepAction[];
  // True when prep_assigned is every task on the meeting rather than only the
  // reader's own, so the UI can label it instead of implying ownership.
  prep_assigned_is_all: boolean;
  // Actions at this meeting whose owner could not be resolved to a person.
  // Shown so an empty list is not misread as "nothing to do".
  unresolved_owner_count: number;
  // True when the meeting has no committee, so earlier actions could not be
  // looked up at all — distinct from having looked and found none.
  committee_missing: boolean;
}

export interface CreatePrepActionRequest {
  description: string;
  assignee_user_id: string;
  due_date?: string | null;
}

export interface CreatePrepCommentRequest {
  file_id: string;
  /** Required unless is_document_level is set. */
  page_number?: number | null;
  is_document_level?: boolean;
  body: string;
}
